
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { MenuIcon, XIcon } from '../components/icons';

import logo from '../logo.png';
import { Sidebar } from '../components/layout/Sidebar';

export default function DashboardLayout({ children }: { children: React.ReactNode }) {
    const { profile, signOut, isSuperAdmin } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    const handleSignOut = async () => {
        await signOut();
        navigate('/login');
    };

    // Check profile.is_active directly OR role === 'superadmin'
    const isActive = profile?.is_active || profile?.role === 'superadmin';
    const shouldShowActivation = !isActive && !isSuperAdmin && profile; // Only show if profile exists

    return (
        <div className="min-h-screen bg-black text-gray-200 flex relative overflow-hidden">
            {/* Mobile Sidebar Overlay */}
            {isSidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/80 z-40 md:hidden backdrop-blur-sm"
                    onClick={() => setIsSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <Sidebar isOpen={isSidebarOpen} />

            {/* Main Content */}
            <main className="flex-1 overflow-hidden relative flex flex-col h-screen">
                {/* Floating Mobile Sidebar Toggle */}
                {!isSidebarOpen && (
                    <button
                        onClick={() => setIsSidebarOpen(true)}
                        className="md:hidden fixed start-0 top-1/2 -translate-y-1/2 z-50 bg-stone-900/90 backdrop-blur-md border border-s-0 border-stone-700 text-stone-300 hover:text-white p-2 sm:p-3 rounded-e-xl shadow-2xl flex items-center justify-center transition-all group"
                    >
                        <div className="flex flex-col gap-[3px] items-center justify-center opacity-80 group-hover:opacity-100 transition-opacity">
                            <span className="w-1 h-1 rounded-full bg-current"></span>
                            <span className="w-1 h-3 rounded-full bg-current"></span>
                            <span className="w-1 h-1 rounded-full bg-current"></span>
                        </div>
                    </button>
                )}

                <div className="flex-1 relative overflow-auto">
                    {shouldShowActivation ? (
                        <div className="absolute inset-0 bg-gray-900/95 z-50 flex flex-col items-center justify-center p-6 text-center">
                            <div className="max-w-md bg-gray-800 p-8 rounded-2xl border border-gray-700 shadow-2xl">
                                <div className="w-16 h-16 bg-yellow-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                </div>
                                <h2 className="text-2xl font-bold text-white mb-2">Account Activation Required</h2>
                                <p className="text-gray-400 mb-6">
                                    Your email is verified, but your account requires manual activation by an administrator to access DOTTING.
                                </p>
                                <a
                                    href="https://wa.me/966550323049?text=Hello%2C%20I%20registered%20on%20DOTTING.%20My%20email%20is%20"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-xl transition-all hover:scale-105"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.463 1.065 2.876 1.213 3.074.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
                                    </svg>
                                    Contact Support via WhatsApp
                                </a>
                            </div>
                        </div>
                    ) : (
                        children
                    )}
                </div>
            </main>
        </div>
    );
}
